import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    // Always show first page
    pageNumbers.push(1);

    // Calculate range around current page
    let rangeStart = Math.max(2, currentPage - 1);
    let rangeEnd = Math.min(totalPages - 1, currentPage + 1);

    // Adjust range to show up to maxPagesToShow pages
    if (rangeEnd - rangeStart < Math.min(maxPagesToShow - 2, totalPages - 2)) {
      if (currentPage < totalPages / 2) {
        rangeEnd = Math.min(
          totalPages - 1,
          rangeStart + Math.min(maxPagesToShow - 2, totalPages - 2)
        );
      } else {
        rangeStart = Math.max(
          2,
          rangeEnd - Math.min(maxPagesToShow - 2, totalPages - 2)
        );
      }
    }

    // Add ellipsis after first page if needed
    if (rangeStart > 2) {
      pageNumbers.push("...");
    }

    // Add pages in range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pageNumbers.push(i);
    }

    // Add ellipsis before last page if needed
    if (rangeEnd < totalPages - 1) {
      pageNumbers.push("...");
    }

    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex justify-center">
      <div className="inline-flex items-center glass-card rounded-2xl p-1 shadow-apple">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-xl font-semibold transition-all duration-200 ${
            currentPage === 1
              ? "bg-apple-gray-100 text-apple-gray-400 cursor-not-allowed"
              : "apple-button-secondary hover-lift"
          }`}
        >
          ← Prev
        </button>

        {/* Page numbers */}
        {pageNumbers.map((page, index) => (
          <React.Fragment key={index}>
            {page === "..." ? (
              <span className="px-3 py-2 text-apple-gray-600 font-semibold">
                ...
              </span>
            ) : (
              <button
                onClick={() => typeof page === "number" && onPageChange(page)}
                className={`px-4 py-2 rounded-xl mx-1 font-semibold transition-all duration-200 ${
                  page === currentPage
                    ? "apple-button-primary"
                    : "glass-button hover-lift text-apple-gray-700"
                }`}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-xl font-semibold transition-all duration-200 ${
            currentPage === totalPages
              ? "bg-apple-gray-100 text-apple-gray-400 cursor-not-allowed"
              : "apple-button-secondary hover-lift"
          }`}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default Pagination;
